<template>
  <hs-modal
    :model-value="true"
    :title="modalTitle"
    :description="`Update the email address associated with this ${entityLabel}. A new verification will be required after saving.`"
    size="sm"
    @close="$emit('close')"
  >
    <form class="email-edit-modal" @submit.prevent="handleSubmit">
      <div class="email-edit-modal__summary">
        <div>
          <span class="email-edit-modal__eyebrow">{{ entityLabel }}</span>
          <p>
            {{ entityName }}
          </p>
        </div>
        <span>{{ currentEmail || "No email set" }}</span>
      </div>

      <hs-input
        v-model="email"
        label="New email address"
        type="email"
        icon="mail"
        placeholder="name@example.com"
        :error="localError || serverError"
        required
        autocomplete="email"
      />
    </form>

    <template #footer>
      <hs-button variant="secondary" @click="$emit('close')">Cancel</hs-button>
      <hs-button type="submit" icon="save" :loading="loading" @click="handleSubmit">
        Save email
      </hs-button>
    </template>
  </hs-modal>
</template>

<script>
import { ref, watch, computed } from "vue";
import { HsButton, HsInput, HsModal } from "@/components/ui";

export default {
  name: "EmailEditModal",
  components: {
    HsButton,
    HsInput,
    HsModal,
  },
  props: {
    entityLabel: {
      type: String,
      required: true,
    },
    entityName: {
      type: String,
      default: "",
    },
    currentEmail: {
      type: String,
      default: "",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    serverError: {
      type: String,
      default: "",
    },
  },
  emits: ["close", "save"],
  setup(props, { emit }) {
    const email = ref(props.currentEmail || "");
    const localError = ref("");

    watch(
      () => props.currentEmail,
      (next) => {
        email.value = next || "";
        localError.value = "";
      }
    );

    const modalTitle = computed(() => `Update ${props.entityLabel} email`);

    const handleSubmit = () => {
      localError.value = "";
      const value = email.value.trim();
      if (!value) {
        localError.value = "Email is required.";
        return;
      }

      emit("save", value);
    };

    return {
      email,
      modalTitle,
      localError,
      handleSubmit,
    };
  },
};
</script>

<style scoped>
.email-edit-modal {
  display: grid;
  gap: 1rem;
}

.email-edit-modal__summary {
  align-items: flex-start;
  background: color-mix(in srgb, var(--hs-bg-soft, #f1f5f9) 72%, var(--hs-surface, #ffffff));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 16px;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  padding: 1rem;
}

.email-edit-modal__summary p {
  color: var(--hs-text, #0f172a);
  font-size: 0.95rem;
  font-weight: 950;
  margin: 0.2rem 0 0;
}

.email-edit-modal__summary > span {
  color: var(--hs-muted, #64748b);
  font-size: 0.8rem;
  font-weight: 850;
  text-align: right;
}

.email-edit-modal__eyebrow {
  color: var(--hs-muted, #64748b);
  font-size: 0.7rem;
  font-weight: 950;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

@media (max-width: 520px) {
  .email-edit-modal__summary {
    display: grid;
  }

  .email-edit-modal__summary > span {
    text-align: left;
  }
}
</style>
